import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box } from "@mui/material";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CustomChip } from "../../../components/chip/Chip";
import { generateColumnsDefaultConfiguration } from "../../../components/dataGrid/columnHelpers";
import { PopoverModal } from "../../../components/popoverModal/PopoverModal";
import { TAB_AUTORIZAR_ID } from "../components/GestionEnviosContent";

const StatusDot = ({ ...props }) => {
  return <CustomChip {...props} sx={{ height: 10, width: 10 }} />;
}

export function useGestionEnviosColumns(
  nombreDelegacion,
  idDelegacion,
  activeTabId
) {
  const navigate = useNavigate();
  const asistencialDetallePath = "/envio/nuevoEnvio";

  const handleClickEdit = (row) => {
    const datosDetalle = {
      idEvaluacion: row.idEvaluacion,
      nombreDelegacion,
      idPaciente: row.idPaciente,
      allowedWrite: true,
      estadosPacienteOptions: [],
      idDelegacion,
      fromScreen: true,
      activeTab: activeTabId
    };

    navigate(asistencialDetallePath, {
      state: {
        datosDetalle,
        isNew: false,
        idOrdenPedido: row.idOrdenPedido,
        idEvaluacion: row.idEvaluacion,
      },
    });
  };

  const handleClickView = (row) => {

    const datosDetalle = {
      idEvaluacion: row.idEvaluacion,
      nombreDelegacion,
      idPaciente: row.idPaciente,
      allowedWrite: false,
      estadosPacienteOptions: [],
      idDelegacion,
      fromScreen: true,
      activeTab: activeTabId
    };
    navigate(asistencialDetallePath, {
      state: {
        datosDetalle,
        isNew: false,
        idOrdenPedido: row.idOrdenPedido,
        idEvaluacion: row.idEvaluacion,
      },
    });
  };

  const getStatusDotUME = (row) => {
    const umePorcentaje = parseFloat(row.umePorcentaje.replace('%', '')) || 0;
    const umeTolerancia = row.umeTolerancia || 0;
    const isGreen = umePorcentaje === 100;
    const isYellow = umePorcentaje === 0 || (umePorcentaje > (100 - umeTolerancia) && umePorcentaje < (100 + umeTolerancia));
    const isRed = !isGreen && !isYellow;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <label style={{ marginRight: 5, marginTop: 2 }}>{row.umePorcentaje}</label>
        <StatusDot isGreen={isGreen} isYellow={isYellow} isRed={isRed} />
      </Box>
    );
  }

  const getStatusDotCS = (row) => {
    const isGreen = row.costoInsumos <= row.costoUnitario;
    const isRed = !isGreen;
    return <StatusDot isGreen={isGreen} isRed={isRed} />;
  }
  const getStatusDotNE = (row) => {
    const cantidadRemitos = row.cantidadRemitos || 0;
    const isGreen = cantidadRemitos === 0;
    const isYellow = cantidadRemitos === 1;
    const isRed = cantidadRemitos > 1;
    return <StatusDot isGreen={isGreen} isYellow={isYellow} isRed={isRed} />;
  }

  const getStatusDotJ = (row) => {
    const isGreen = row.condicional === "" || (row.condicional === "C" && (row.estadoMedico === "AP" && row.estadoComercial === "AP" && (row.estadoBUM === "" || row.estadoBUM === "AP"))) || !row.estadoPEnvio;
    const isRed = row.condicional === "C" && (row.estadoMedico === "RE" || row.estadoComercial === "RE" || row.estadoBUM === "RE");
    const isYellow = row.condicional === "C" && ((row.estadoMedico === "PE" || row.estadoComercial === "PE" || row.estadoBUM === "PE") && row.estadoPEnvio);

    if (row.condicional === "C") {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ marginRight: 5, marginTop: 2 }}>C</label>
          <StatusDot isGreen={isGreen} isYellow={isYellow} isRed={isRed} />
        </Box>
      )
    }
    return <StatusDot isGreen={isGreen} isYellow={isYellow} isRed={isRed} />;
  }

  const initialColumns = [
    {
      headerName: "",
      field: "action",
      align: 'left',
      flex: 35,
      minWidth: 35,
      maxWidth: 35,
      noPadding: true,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <PopoverModal>
            <div className="popover-modal-content">
              <div
                onClick={() => handleClickView(row)} aria-hidden='true'
                className="item-option clickable-option"
              >
                <VisibilityIcon className="edit-icon" />
                <span className="toggle-action">Ver</span>
              </div>
              {/* {activeTabId === 0 &&  */}
              <div
                onClick={() => handleClickEdit(row)} aria-hidden='true'
                className="item-option clickable-option"
              >
                <EditIcon className="edit-icon" />
                <span className="toggle-action">Editar</span>
              </div>
              {/* } */}
            </div>
          </PopoverModal>
        );
      },
    },
    {
      flex: 40,
      field: "idOrdenPedido",
      headerName: "#",
    },
    {
      flex: 40,
      field: "codSAP",
      headerName: "#SAP",
    },
    {
      flex: 85,
      field: "delegacion",
      headerName: "Delegación",
    },
    {
      flex: 45,
      field: "idEvaluacion",
      headerName: "Contrato",
    },
    {
      flex: 40,
      field: "idPaciente",
      headerName: "HC",
    },
    {
      flex: 110,
      field: "paciente",
      headerName: "Paciente",
    },
    {
      flex: 60,
      field: "fechaEntrega",
      headerName: "F. Entrega",
      type: 'date'
    },
    {
      flex: 70,
      field: "fechaAutorizar",
      headerName: activeTabId === TAB_AUTORIZAR_ID ? "F. Autorizar" : "F. Autorizador",
      type: 'date',
      time: true,
      valueGetter: (_, row) => (
        activeTabId === TAB_AUTORIZAR_ID ? row?.fechaAutorizar : row?.fechaAutorizado
      ),
    },
    {
      flex: 85,
      field: "usuarioAutorizar",
      headerName: activeTabId === TAB_AUTORIZAR_ID ? "Usuario Autorizar" : "Usuario Autorizador",
      valueGetter: (_, row) => (
        activeTabId === TAB_AUTORIZAR_ID ? row?.nombreUsuarioAutorizar : row?.nombreUsuarioAutorizado
      ),
    },
    {
      flex: 45,
      field: "UME",
      headerName: "UME%",
      soloAAutorizar: true,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getStatusDotUME(row);
      }
    },
    {
      flex: 20,
      minWidth: 30,
      maxWidth: 30,
      field: "cs",
      headerName: "CS",
      soloAAutorizar: true,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getStatusDotCS(row);
      }
    },
    {
      flex: 20,
      minWidth: 30,
      maxWidth: 30,
      field: "ne",
      headerName: "NE",
      soloAAutorizar: true,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getStatusDotNE(row);
      }
    },
    {
      flex: 20,
      minWidth: 40,
      maxWidth: 40,
      field: "j",
      headerName: "J",
      align: 'center',
      headerAlign: 'center',
      soloAAutorizar: true,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getStatusDotJ(row);
      }
    },
  ];

  const columns = useMemo(
    () => generateColumnsDefaultConfiguration(
      initialColumns.filter(x => !x.soloAAutorizar || activeTabId === 0),
      true
    ),
    [activeTabId]
  );

  return columns;
}
